import { useCallback, useState } from 'react';

const STORAGE_KEY_PREFIX = 'hero_scoring_section_open:';

// React state for a HeroScoringRowsSection accordion's open/closed value, persisted to localStorage so
// that the user's expand/collapse choice survives navigation between hero pages. The persisted value
// takes precedence over `defaultOpen` on subsequent visits; `defaultOpen` only applies before any toggle.
// Intentionally not user/org-scoped: this is a UI preference, not user data, and the resulting bleed
// across accounts on a shared browser is harmless (still just an accordion default).
function useSectionOpenState(id: string, defaultOpen: boolean): readonly [boolean, (next: boolean) => void] {
  const storageKey = `${STORAGE_KEY_PREFIX}${id}`;

  const [open, setOpenState] = useState<boolean>(() => {
    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored === 'true') return true;
      if (stored === 'false') return false;
    } catch {
      // localStorage unavailable (private mode, SSR, etc.) — fall through to default.
    }
    return defaultOpen;
  });

  const setOpen = useCallback(
    (next: boolean) => {
      setOpenState(next);
      try {
        window.localStorage.setItem(storageKey, String(next));
      } catch {
        // Storage write failed (quota, private mode); keep the in-memory state.
      }
    },
    [storageKey],
  );

  return [open, setOpen] as const;
}

export { useSectionOpenState };
